import jwtDecode from 'jwt-decode';
import { ROLE_TYPES } from '../constants/roleTypes';
import { JwtHelper } from './jwt';

const ADMIN_TOKEN_KEY = 'admin_token';
export const IMPERSONATION_EXPIRED_FLAG = 'impersonation_expired';
export const IMPERSONATION_READ_ONLY_MESSAGE =
    '代理ログイン中は閲覧のみ可能です。登録・変更・削除などの操作はできません。';

export const impersonateUser = ({ targetId, token } = {}) => {
    if (!token) return;

    const currentToken = localStorage.getItem('token');
    if (currentToken) {
        localStorage.setItem(ADMIN_TOKEN_KEY, currentToken);
    }

    localStorage.setItem('token', token);

    // Kept for display / migration; principal comes from the impersonation JWT.
    if (targetId) {
        localStorage.setItem('target_user_id', targetId);
    }

    window.location.reload();
};

export const clearImpersonationState = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem('target_user_id');
};

/**
 * Impersonation JWT expired/invalid → restore admin session (do NOT full logout).
 * @param {{ reload?: boolean, expired?: boolean }} options
 *   expired: set a flag so UI can show toast + reopen impersonation dialog after reload
 * @returns {boolean} true if admin session was restored
 */
export const restoreAdminFromImpersonation = ({ reload = true, expired = false } = {}) => {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!adminToken || !JwtHelper.isValid(adminToken)) {
        return false;
    }

    localStorage.setItem('token', adminToken);
    clearImpersonationState();

    if (expired) {
        sessionStorage.setItem(IMPERSONATION_EXPIRED_FLAG, '1');
    }

    if (reload) {
        window.location.reload();
    }

    return true;
};

/** Read and clear the “impersonation expired” flag (survives one reload via sessionStorage). */
export const consumeImpersonationExpiredFlag = () => {
    const expired = sessionStorage.getItem(IMPERSONATION_EXPIRED_FLAG) === '1';
    if (expired) {
        sessionStorage.removeItem(IMPERSONATION_EXPIRED_FLAG);
    }
    return expired;
};

/**
 * If currently impersonating and the impersonation JWT is past exp, end the session.
 * Used by a client-side timer so expiry does not wait for the next API 401.
 */
export const expireImpersonationIfNeeded = () => {
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) return false;

    const token = localStorage.getItem('token');
    if (token && JwtHelper.isValid(token)) return false;

    return restoreAdminFromImpersonation({ reload: true, expired: true });
};

/** ms until current (impersonation) token expires; 0 if already expired; null if unknown. */
export const getMsUntilTokenExpiry = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    const expiresAt = JwtHelper.expiresAt(token);
    const ms = expiresAt instanceof Date ? expiresAt.getTime() - Date.now() : NaN;
    if (Number.isNaN(ms)) return null;
    return Math.max(0, ms);
};

export const stopImpersonating = () => {
    // Manual exit — no expired toast / dialog
    if (restoreAdminFromImpersonation({ reload: true, expired: false })) {
        return;
    }

    clearImpersonationState();
    window.location.reload();
};

export const isImpersonating = () => {
    if (localStorage.getItem(ADMIN_TOKEN_KEY)) return true;

    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
        const decoded = jwtDecode(token);
        return !!decoded?.isImpersonating;
    } catch (error) {
        return false;
    }
};

const getAdminOrCurrentToken = () => {
    return localStorage.getItem(ADMIN_TOKEN_KEY) || localStorage.getItem('token');
};

export const isAdminUser = () => {
    const token = getAdminOrCurrentToken();
    if (!token) return false;

    try {
        const decoded = jwtDecode(token);
        return decoded?.roles?.includes(ROLE_TYPES.ADMIN);
    } catch (error) {
        return false;
    }
};
